/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useRef } from 'react';
import {
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  X,
  FileText,
  Sparkles,
  Calendar,
  Layers,
  Users,
  ShieldCheck,
  Building2,
  Trash2,
  RefreshCw,
  Info,
  ArrowRight
} from 'lucide-react';
import { CostCenter, SafetyTalk, SafetyTalkStatus } from '../types.ts';
import {
  generateSafetyTalksTemplateExcel,
  generateSafetyTalksTemplateCSV,
  parseSafetyTalksFile,
  ParsedSafetyTalksResult
} from '../utils/safetyTalksExcelHelper.ts';

interface SafetyTalksBulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeCostCenter: CostCenter | null;
  selectedYear: number;
  selectedMonthIndex: number;
  existingMonthTalks: SafetyTalk[];
  onImportTalks: (talks: SafetyTalk[], replaceMonth: boolean) => void;
}

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export const SafetyTalksBulkUploadModal: React.FC<SafetyTalksBulkUploadModalProps> = ({
  isOpen,
  onClose,
  activeCostCenter,
  selectedYear,
  selectedMonthIndex,
  existingMonthTalks,
  onImportTalks
}) => {
  const [activeTab, setActiveTab] = useState<'upload' | 'template' | 'guide'>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<ParsedSafetyTalksResult | null>(null);
  const [importMode, setImportMode] = useState<'merge' | 'replace'>('merge');
  const [searchPreview, setSearchPreview] = useState('');

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (!isOpen) return null;

  const currentMonthName = MONTH_NAMES[selectedMonthIndex] || 'Mes Actual';
  const costCenterLabel = activeCostCenter ? activeCostCenter.name : 'Sede Bajío - Salamanca';

  const handleDownloadExcelTemplate = async () => {
    setIsDownloading(true);
    try {
      await generateSafetyTalksTemplateExcel(selectedYear, selectedMonthIndex, costCenterLabel);
    } catch (err) {
      console.error('Error generating template:', err);
      if (window.showAppAlert) {
        window.showAppAlert('Ocurrió un error al generar la plantilla de Excel.');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadCsvTemplate = () => {
    generateSafetyTalksTemplateCSV(selectedYear, selectedMonthIndex);
  };

  const handleFileProcess = async (file: File) => {
    const ext = file.name.toLowerCase();
    if (!ext.endsWith('.xlsx') && !ext.endsWith('.xls') && !ext.endsWith('.csv')) {
      if (window.showAppAlert) {
        window.showAppAlert('Formato de archivo no soportado. Por favor seleccione un archivo Excel (.xlsx, .xls) o CSV (.csv).');
      }
      return;
    }

    setUploadedFile(file);
    setIsParsing(true);
    try {
      const res = await parseSafetyTalksFile(
        file,
        activeCostCenter?.id || 'CC-MAZDA-BAJIO',
        activeCostCenter?.name || costCenterLabel
      );
      setParseResult(res);
      setActiveTab('upload');
    } catch (err) {
      console.error('Error parsing safety talks file:', err);
      if (window.showAppAlert) {
        window.showAppAlert('No fue posible leer el archivo. Verifique que no esté protegido o dañado.');
      }
    } finally {
      setIsParsing(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileProcess(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileProcess(e.target.files[0]);
    }
  };

  const handleResetUpload = () => {
    setUploadedFile(null);
    setParseResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleConfirmImport = () => {
    if (!parseResult || parseResult.validTalks.length === 0) return;

    onImportTalks(parseResult.validTalks, importMode === 'replace');
    onClose();

    if (window.showAppAlert) {
      window.showAppAlert(
        `Se han importado exitosamente ${parseResult.validTalks.length} pláticas de 5 minutos al calendario de ${currentMonthName} ${selectedYear}.`
      );
    }
  };

  const filteredPreview = parseResult
    ? parseResult.validTalks.filter((t) => {
        if (!searchPreview) return true;
        const q = searchPreview.toLowerCase();
        return (
          t.topic.toLowerCase().includes(q) ||
          t.date.toLowerCase().includes(q) ||
          t.facilitator.toLowerCase().includes(q) ||
          t.area.toLowerCase().includes(q) ||
          (t.standardCode && t.standardCode.toLowerCase().includes(q))
        );
      })
    : [];

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-3 sm:p-5 backdrop-blur-sm animate-in fade-in">
      <div className="bg-[#13141b] border-2 border-[#2b2e40] rounded-3xl max-w-5xl w-full max-h-[92vh] flex flex-col text-white shadow-2xl overflow-hidden">
        
        {/* Hidden Native File Input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx,.xls,.csv"
          className="hidden"
        />

        {/* Modal Header */}
        <div className="p-5 border-b border-[#252736] bg-[#0e0f14] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-600/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shrink-0">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-bold text-white">
                  Carga Masiva de Pláticas de 5 Minutos
                </h3>
                <span className="text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full bg-emerald-950 text-emerald-300 border border-emerald-500/50 font-mono">
                  NOM-019-STPS
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-[#1f2230] text-slate-300 border border-[#34384e]">
                  {currentMonthName} {selectedYear}
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Genere o descargue la plantilla Excel/CSV, complete la programación mensual y cárguela en segundos.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 hover:text-white transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs Bar */}
        <div className="px-5 pt-3 bg-[#111218] border-b border-[#222432] flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveTab('upload')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-t-xl text-xs font-bold transition-all cursor-pointer border-b-2 ${
                activeTab === 'upload'
                  ? 'border-emerald-500 text-emerald-300 bg-[#171922]'
                  : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
            >
              <Upload className="w-3.5 h-3.5" />
              1. Carga & Previsualización
              {parseResult && (
                <span className="ml-1 px-1.5 py-0.2 text-[10px] rounded-full bg-emerald-900 text-emerald-200 font-mono">
                  {parseResult.validTalks.length}
                </span>
              )}
            </button>

            <button
              onClick={() => setActiveTab('template')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-t-xl text-xs font-bold transition-all cursor-pointer border-b-2 ${
                activeTab === 'template'
                  ? 'border-emerald-500 text-emerald-300 bg-[#171922]'
                  : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
            >
              <Download className="w-3.5 h-3.5" />
              2. Descargar Plantilla Oficial
            </button>

            <button
              onClick={() => setActiveTab('guide')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-t-xl text-xs font-bold transition-all cursor-pointer border-b-2 ${
                activeTab === 'guide'
                  ? 'border-emerald-500 text-emerald-300 bg-[#171922]'
                  : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
            >
              <Info className="w-3.5 h-3.5" />
              Instrucciones & Normas STPS
            </button>
          </div>

          <div className="text-[11px] text-slate-400 pb-2 flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5 text-emerald-400" />
            <span>Sede: <strong className="text-slate-200">{costCenterLabel}</strong></span>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          
          {/* TAB 1: UPLOAD & PREVIEW */}
          {activeTab === 'upload' && (
            <div className="space-y-4">
              
              {/* If no file uploaded yet, show Drop Zone */}
              {!parseResult && (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-3xl p-8 text-center transition-all cursor-pointer ${
                    isDragging
                      ? 'border-emerald-400 bg-emerald-950/30 scale-[1.01]'
                      : 'border-[#2d3040] hover:border-emerald-500/60 bg-[#101117] hover:bg-[#151720]'
                  }`}
                >
                  <div className="max-w-md mx-auto space-y-3">
                    <div className="w-16 h-16 rounded-3xl bg-emerald-600/10 border border-emerald-500/30 flex items-center justify-center mx-auto text-emerald-400">
                      {isParsing ? (
                        <RefreshCw className="w-8 h-8 animate-spin" />
                      ) : (
                        <Upload className="w-8 h-8" />
                      )}
                    </div>

                    <div>
                      <h4 className="text-base font-bold text-white">
                        {isParsing ? 'Procesando archivo...' : 'Arrastre y suelte su archivo Excel o CSV aquí'}
                      </h4>
                      <p className="text-xs text-slate-400 mt-1">
                        Compatible con <span className="text-emerald-300 font-mono font-bold">.xlsx</span>, <span className="text-emerald-300 font-mono font-bold">.xls</span> y <span className="text-emerald-300 font-mono font-bold">.csv</span>
                      </p>
                    </div>

                    <div className="pt-2 flex items-center justify-center gap-3 flex-wrap">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-lg shadow-emerald-600/20 transition-all cursor-pointer"
                      >
                        <FileSpreadsheet className="w-4 h-4" />
                        Seleccionar Archivo de mi Equipo
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadExcelTemplate();
                        }}
                        className="px-4 py-2 bg-[#1b1d28] hover:bg-[#252838] border border-[#32364c] text-slate-200 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                      >
                        <Download className="w-3.5 h-3.5 text-emerald-400" />
                        Descargar Plantilla Primero
                      </button>
                    </div>

                    <p className="text-[11px] text-slate-500 pt-2">
                      ¿No tiene la plantilla? Descárguela con un clic para obtener las columnas preconfiguradas y ejemplos de temas STPS.
                    </p>
                  </div>
                </div>
              )}

              {/* Once file is parsed: Show detailed Summary & Preview Table */}
              {parseResult && (
                <div className="space-y-4 animate-in fade-in">
                  
                  {/* File Banner & Action Toolbar */}
                  <div className="bg-[#101117] border border-[#272938] rounded-2xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-600/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shrink-0">
                        <FileSpreadsheet className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-white truncate max-w-xs">
                            {uploadedFile?.name || 'Archivo Cargado'}
                          </span>
                          <span className="text-[10px] font-mono px-2 py-0.2 rounded bg-slate-800 text-slate-300">
                            {uploadedFile ? `${(uploadedFile.size / 1024).toFixed(1)} KB` : ''}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400">
                          {parseResult.validTalks.length} pláticas válidas detectadas • {parseResult.invalidRows.length} filas con observaciones
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleResetUpload}
                        className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer border border-slate-700"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        <span>Cargar Otro Archivo</span>
                      </button>
                    </div>
                  </div>

                  {/* KPI Status Strip */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bg-[#101117] border border-emerald-500/30 rounded-2xl p-3 flex items-center justify-between">
                      <div>
                        <div className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">
                          Pláticas Válidas a Importar
                        </div>
                        <div className="text-xl font-black text-emerald-300 font-mono">
                          {parseResult.validTalks.length}
                        </div>
                      </div>
                      <div className="w-8 h-8 rounded-xl bg-emerald-950/80 border border-emerald-500/40 flex items-center justify-center text-emerald-400">
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                    </div>

                    <div className="bg-[#101117] border border-blue-500/30 rounded-2xl p-3 flex items-center justify-between">
                      <div>
                        <div className="text-[10px] font-bold text-blue-400 uppercase tracking-wider">
                          Pláticas Existentes del Mes
                        </div>
                        <div className="text-xl font-black text-blue-300 font-mono">
                          {existingMonthTalks.length}
                        </div>
                      </div>
                      <div className="w-8 h-8 rounded-xl bg-blue-950/80 border border-blue-500/40 flex items-center justify-center text-blue-400">
                        <Calendar className="w-4 h-4" />
                      </div>
                    </div>

                    <div className="bg-[#101117] border border-[#272a38] rounded-2xl p-3 flex items-center justify-between">
                      <div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                          Filas con Error / Omitidas
                        </div>
                        <div className={`text-xl font-black font-mono ${parseResult.invalidRows.length > 0 ? 'text-amber-400' : 'text-slate-400'}`}>
                          {parseResult.invalidRows.length}
                        </div>
                      </div>
                      <div className="w-8 h-8 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400">
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                      </div>
                    </div>
                  </div>

                  {/* Mode Selector */}
                  <div className="p-3.5 bg-[#101117] border border-[#272938] rounded-2xl space-y-2">
                    <label className="text-xs font-bold text-slate-300 block">
                      Seleccione el método de importación:
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setImportMode('merge')}
                        className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex items-start gap-2.5 ${
                          importMode === 'merge'
                            ? 'bg-emerald-950/40 border-emerald-500/80 text-white'
                            : 'bg-[#141620] border-[#292c3d] text-slate-400 hover:text-white'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded-full mt-0.5 border flex items-center justify-center shrink-0 ${
                          importMode === 'merge' ? 'border-emerald-400 bg-emerald-500' : 'border-slate-600'
                        }`}>
                          {importMode === 'merge' && <span className="w-1.5 h-1.5 rounded-full bg-black"></span>}
                        </div>
                        <div>
                          <div className="text-xs font-bold text-emerald-300">Conservar y Fusionar con Existentes</div>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            Agrega las nuevas pláticas al calendario. Si una fecha ya existe, se complementa sin borrar las previas.
                          </p>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setImportMode('replace')}
                        className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex items-start gap-2.5 ${
                          importMode === 'replace'
                            ? 'bg-amber-950/40 border-amber-500/80 text-white'
                            : 'bg-[#141620] border-[#292c3d] text-slate-400 hover:text-white'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded-full mt-0.5 border flex items-center justify-center shrink-0 ${
                          importMode === 'replace' ? 'border-amber-400 bg-amber-500' : 'border-slate-600'
                        }`}>
                          {importMode === 'replace' && <span className="w-1.5 h-1.5 rounded-full bg-black"></span>}
                        </div>
                        <div>
                          <div className="text-xs font-bold text-amber-300">Reemplazar Todo el Mes</div>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            Limpia la planeación actual del mes ({currentMonthName} {selectedYear}) y carga exclusivamente las del archivo.
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Invalid Rows Warnings Accordion (If any) */}
                  {parseResult.invalidRows.length > 0 && (
                    <div className="p-3 bg-amber-950/30 border border-amber-500/40 rounded-2xl space-y-1.5 text-xs">
                      <div className="flex items-center gap-2 font-bold text-amber-300">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <span>Advertencia: {parseResult.invalidRows.length} fila(s) no pudieron ser procesadas y serán ignoradas</span>
                      </div>
                      <ul className="list-disc list-inside text-[11px] text-slate-300 space-y-0.5 pl-2 max-h-24 overflow-y-auto">
                        {parseResult.invalidRows.map((inv, i) => (
                          <li key={i}>
                            Fila #{inv.rowNumber}: <span className="text-amber-200">{inv.reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Preview Table Header & Search Filter */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 pt-1">
                    <h4 className="text-xs font-bold text-white flex items-center gap-1.5">
                      <span>Previsualización de Pláticas ({filteredPreview.length} de {parseResult.validTalks.length})</span>
                    </h4>

                    <input
                      type="text"
                      placeholder="Filtrar por tema, fecha, facilitador..."
                      value={searchPreview}
                      onChange={(e) => setSearchPreview(e.target.value)}
                      className="w-full sm:w-64 bg-[#101115] border border-[#2b2d3d] rounded-xl px-3 py-1 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                    />
                  </div>

                  {/* Preview Table */}
                  <div className="bg-[#101115] border border-[#262838] rounded-2xl overflow-hidden max-h-72 overflow-y-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-[#171922] text-slate-300 font-bold border-b border-[#262838] sticky top-0 z-10 text-[11px]">
                        <tr>
                          <th className="p-2.5">Fecha / Turno</th>
                          <th className="p-2.5">Tema de la Plática</th>
                          <th className="p-2.5">Norma STPS</th>
                          <th className="p-2.5">Facilitador / Área</th>
                          <th className="p-2.5 text-center">Asistentes</th>
                          <th className="p-2.5 text-center">Estatus</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#1f212d]">
                        {filteredPreview.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="p-6 text-center text-slate-500 text-xs">
                              No hay registros que coincidan con la búsqueda.
                            </td>
                          </tr>
                        ) : (
                          filteredPreview.map((talk, idx) => (
                            <tr key={idx} className="hover:bg-[#161823] transition-colors">
                              <td className="p-2.5 font-mono text-slate-300 whitespace-nowrap">
                                <div className="font-bold text-white">{talk.date}</div>
                                <div className="text-[10px] text-slate-400">{talk.time || '07:30'} • {talk.shift}</div>
                              </td>
                              <td className="p-2.5 text-slate-200">
                                <div className="font-bold text-white">{talk.topic}</div>
                                {talk.objective && (
                                  <div className="text-[10px] text-slate-400 truncate max-w-xs">{talk.objective}</div>
                                )}
                              </td>
                              <td className="p-2.5 whitespace-nowrap">
                                <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700 font-mono text-[10px]">
                                  {talk.standardCode || 'NOM-017-STPS'}
                                </span>
                              </td>
                              <td className="p-2.5 whitespace-nowrap">
                                <div className="font-bold text-slate-200">{talk.facilitator}</div>
                                <div className="text-[10px] text-slate-400">{talk.area}</div>
                              </td>
                              <td className="p-2.5 text-center font-mono text-slate-300">
                                <span className="px-2 py-0.5 rounded-full bg-purple-950 text-purple-300 border border-purple-800 text-[10px] font-bold">
                                  {talk.attendeesCount} pax
                                </span>
                              </td>
                              <td className="p-2.5 text-center whitespace-nowrap">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase font-mono ${
                                  talk.status === 'aplicada'
                                    ? 'bg-emerald-950 text-emerald-300 border border-emerald-600'
                                    : talk.status === 'programada'
                                    ? 'bg-blue-950 text-blue-300 border border-blue-600'
                                    : 'bg-amber-950 text-amber-300 border border-amber-600'
                                }`}>
                                  {talk.status}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                </div>
              )}

            </div>
          )}

          {/* TAB 2: TEMPLATE GENERATOR & DOWNLOAD */}
          {activeTab === 'template' && (
            <div className="space-y-4 animate-in fade-in">
              <div className="bg-gradient-to-r from-emerald-950/60 via-[#14231f] to-[#12131a] border-2 border-emerald-500/40 rounded-3xl p-6 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-600/20 border border-emerald-500/50 flex items-center justify-center text-emerald-400 shrink-0">
                    <FileSpreadsheet className="w-6 h-6" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="text-base font-bold text-white">
                      Plantilla Oficial de Pláticas de 5 Minutos (Formato STPS)
                    </h4>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      Descargue el archivo de hoja de cálculo prediseñado con todas las cabeceras requeridas, validaciones de fecha, catálogo de normas oficiales mexicanas y filas de ejemplo para su sede.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  {/* Excel (.xlsx) Card */}
                  <div className="bg-[#101115] border border-emerald-500/30 rounded-2xl p-4 flex flex-col justify-between space-y-3">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-emerald-300 flex items-center gap-1.5">
                          <FileSpreadsheet className="w-4 h-4" /> Formato Microsoft Excel (.xlsx)
                        </span>
                        <span className="text-[10px] font-bold bg-emerald-950 text-emerald-300 px-2 py-0.5 rounded border border-emerald-500/40">
                          Recomendado
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1">
                        Incluye 3 pestañas: Plantilla de Carga, Catálogo de Normas STPS y Guía de Llenado con colores corporativos.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleDownloadExcelTemplate}
                      disabled={isDownloading}
                      className="w-full py-2.5 px-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/30 transition-all cursor-pointer"
                    >
                      <Download className="w-4 h-4" />
                      {isDownloading ? 'Generando Plantilla...' : `Descargar Plantilla Excel (${currentMonthName} ${selectedYear})`}
                    </button>
                  </div>

                  {/* CSV Card */}
                  <div className="bg-[#101115] border border-[#272938] rounded-2xl p-4 flex flex-col justify-between space-y-3">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-200 flex items-center gap-1.5">
                          <FileText className="w-4 h-4 text-sky-400" /> Formato Plano Delimitado (.csv)
                        </span>
                        <span className="text-[10px] font-mono text-slate-400 px-2 py-0.5 rounded bg-slate-800">
                          UTF-8
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1">
                        Ideal para importar/exportar datos desde ERPs externos, Google Sheets o sistemas automatizados.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleDownloadCsvTemplate}
                      className="w-full py-2.5 px-4 bg-[#1b1d28] hover:bg-[#252838] border border-[#32364c] text-slate-200 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer"
                    >
                      <Download className="w-4 h-4 text-sky-400" />
                      Descargar Plantilla CSV (.csv)
                    </button>
                  </div>
                </div>
              </div>

              {/* Column Structure Cheat Sheet */}
              <div className="bg-[#101115] border border-[#272938] rounded-2xl p-4 space-y-2">
                <h5 className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
                  <Info className="w-4 h-4 text-emerald-400" /> Columnas y Estructura del Archivo:
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
                  <div className="p-2 rounded-xl bg-[#161720] border border-[#282a3a]">
                    <span className="font-bold text-emerald-300">1. Fecha (*):</span>
                    <p className="text-slate-400">Formato <code className="text-white">AAAA-MM-DD</code> (ej: 2026-08-05).</p>
                  </div>
                  <div className="p-2 rounded-xl bg-[#161720] border border-[#282a3a]">
                    <span className="font-bold text-emerald-300">2. Hora & Turno:</span>
                    <p className="text-slate-400">Ej: 07:30, Matutino / Vespertino.</p>
                  </div>
                  <div className="p-2 rounded-xl bg-[#161720] border border-[#282a3a]">
                    <span className="font-bold text-emerald-300">3. Tema (*):</span>
                    <p className="text-slate-400">Nombre descriptivo de la plática.</p>
                  </div>
                  <div className="p-2 rounded-xl bg-[#161720] border border-[#282a3a]">
                    <span className="font-bold text-emerald-300">4. Norma STPS:</span>
                    <p className="text-slate-400">Ej: NOM-017-STPS / EPP.</p>
                  </div>
                  <div className="p-2 rounded-xl bg-[#161720] border border-[#282a3a]">
                    <span className="font-bold text-emerald-300">5. Facilitador (*):</span>
                    <p className="text-slate-400">Nombre de quien imparte la plática.</p>
                  </div>
                  <div className="p-2 rounded-xl bg-[#161720] border border-[#282a3a]">
                    <span className="font-bold text-emerald-300">6. Estatus (*):</span>
                    <p className="text-slate-400"><code className="text-white">programada</code> o <code className="text-white">aplicada</code>.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: GUIDE & STPS STANDARDS */}
          {activeTab === 'guide' && (
            <div className="space-y-4 animate-in fade-in">
              <div className="bg-[#101115] border border-[#272938] rounded-2xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                  <h4 className="text-xs font-bold text-white uppercase tracking-wider font-mono">
                    Temas Recomendados de Pláticas de 5 Minutos según Normatividad STPS
                  </h4>
                </div>
                <p className="text-xs text-slate-400">
                  Puede copiar directamente cualquiera de estos temas oficiales en su plantilla de Excel para mantener un programa integral de capacitación diaria:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 pt-1 text-xs">
                  {[
                    { norm: 'NOM-017-STPS-2008', title: 'Uso y Conservación del Equipo de Protección Personal (EPP)', area: 'Planta General' },
                    { norm: 'NOM-009-STPS-2011', title: 'Prevención de Caídas y Trabajos Seguros en Alturas', area: 'Mantenimiento / Alturas' },
                    { norm: 'NOM-018-STPS-2015', title: 'Identificación y Comunicación de Peligros Químicos (SGA)', area: 'Decapado y Químicos' },
                    { norm: 'NOM-004-STPS-1999', title: 'Sistemas de Protección y Dispositivos de Bloqueo LOTO', area: 'Hornos y Maquinaria' },
                    { norm: 'NOM-005-STPS-1998', title: 'Manejo Seguro de Sustancias Inflamables y Combustibles', area: 'Pintura y Solventes' },
                    { norm: 'NOM-002-STPS-2010', title: 'Prevención de Incendios, Extintores y Evacuación', area: 'Todas las Áreas' },
                    { norm: 'NOM-036-1-STPS-2018', title: 'Ergonomía e Higiene Postural en Manejo Manual de Cargas', area: 'Almacén y Embarques' },
                    { norm: 'NOM-026-STPS-2008', title: 'Colores y Señales de Seguridad e Higiene Industrial', area: 'Patios y Tránsito' },
                    { norm: 'NOM-033-STPS-2015', title: 'Seguridad en Trabajos en Espacios Confinados', area: 'Mantenimiento Mayor' },
                    { norm: 'NOM-006-STPS-2014', title: 'Manejo Seguro y Operación de Montacargas', area: 'Logística y Patios' },
                    { norm: 'NOM-035-STPS-2018', title: 'Factores de Riesgo Psicosocial y Entorno Favorable', area: 'Administración y Planta' },
                    { norm: 'ISO 45001', title: 'Metodología 5S: Orden y Limpieza como Barrera Preventiva', area: 'Piso de Producción' }
                  ].map((s, idx) => (
                    <div key={idx} className="p-2.5 rounded-xl bg-[#161720] border border-[#252838] flex items-start gap-2">
                      <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-950 text-emerald-300 border border-emerald-500/30 shrink-0">
                        {s.norm}
                      </span>
                      <div>
                        <div className="font-bold text-slate-200 text-xs">{s.title}</div>
                        <div className="text-[10px] text-slate-400">Enfoque: {s.area}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-[#252736] bg-[#0e0f14] flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-slate-400 flex items-center gap-2">
            {parseResult ? (
              <span>
                Listo para procesar <strong className="text-emerald-400">{parseResult.validTalks.length}</strong> pláticas
              </span>
            ) : (
              <span>Seleccione o arrastre un archivo para habilitar la importación</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold cursor-pointer transition-colors"
            >
              Cancelar
            </button>

            {parseResult && parseResult.validTalks.length > 0 && (
              <button
                onClick={handleConfirmImport}
                className="px-5 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-lg shadow-emerald-600/30 transition-all cursor-pointer transform active:scale-95"
              >
                <CheckCircle2 className="w-4 h-4" />
                Importar {parseResult.validTalks.length} Pláticas al Calendario
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};
